import React, { useEffect, useState, useCallback } from "react";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  useParams,
  useNavigate,
  useLocation,
  useSearchParams,
} from "react-router-dom";
import { useMemo } from "react";
import ListSubheader from "@mui/material/ListSubheader";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  Button,
  TextField,
  Typography,
  CircularProgress,
  Divider,
  List,
  ListItem,
  ListItemText,
  Paper,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Alert,
  Grid,
  Checkbox,
  FormControlLabel,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Backdrop,
} from "@mui/material";
import { RootState, AppDispatch } from "../../../redux/store";
import {
  fetchDeployment,
  fetchDeployments,
  updateDeployment,
} from "../../../redux/actions/deploymentActions";
import {
  fetchAllBuilds,
  fetchUnreleasedS3Builds,
  createHotBuildFromS3,
} from "../../../redux/actions/buildActions";
import { fetchAllBehaviorConfigs } from "../../../redux/actions/behaviorConfigActions";
import { fetchOrganization } from "../../../redux/actions/organizationActions";

const DeploymentView: React.FC = () => {
  const { deploymentId } = useParams<{ deploymentId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch<AppDispatch>();
  const [searchParams] = useSearchParams();
  const organizationId = searchParams.get("organization_id");

  const { currentDeployment, deployments, loading, error } = useSelector(
    (state: RootState) => state.deployments
  );

  const { builds, loading: buildLoading, uncutBuilds } = useSelector(
    (state: RootState) => state.builds
  );

  const { behaviorConfigs, loading: behaviorConfigLoading } = useSelector(
    (state: RootState) => state.behaviorConfigs
  );

  const { currentOrganization } = useSelector(
    (state: RootState) => state.organizations
  );

  const [isEditing, setIsEditing] = useState(false);
  const [minHosts, setMinHosts] = useState<number | undefined>(undefined);
  const [maxHosts, setMaxHosts] = useState<number | undefined>(undefined);
  const [buildSelection, setBuildSelection] = useState<string>(""); // "registry:<id>" | "uncut:<s3_path-encoded>"
  const [behaviorConfigId, setBehaviorConfigId] = useState("");
  const [minHostsIsNone, setMinHostsIsNone] = useState(false);
  const [maxHostsIsNone, setMaxHostsIsNone] = useState(false);
  const [hostValidationError, setHostValidationError] = useState("");
  const [promoteDialogOpen, setPromoteDialogOpen] = useState(false);
  const [disablePhantomSessions, setDisablePhantomSessions] = useState(false);

  const [isSaving, setIsSaving] = useState(false);
  const [isPromoting, setIsPromoting] = useState(false);

  const hotRegistryBuilds = useMemo(
    () => (builds ?? []).filter((b) => b.s3_build_is_hot === true),
    [builds]
  );

  // Get the current build object to display its name
  const currentBuild = useMemo(
    () => builds?.find((b) => b.id === currentDeployment?.build_id),
    [builds, currentDeployment?.build_id]
  );

  // Get the deployment chain (previous deployments)
  const deploymentChain = deployments
    .filter((d) => d.organization_id === organizationId)
    .sort(
      (a, b) =>
        new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
    );

  // Determine if this is the latest deployment for the organization
  const isLatestDeployment =
    deploymentChain.length > 0 &&
    deploymentId &&
    deploymentChain[0].id === deploymentId;

  useEffect(() => {
    // Load all necessary data
    if (deploymentId && organizationId) {
      dispatch(fetchDeployment({ deploymentId, organizationId }));
      dispatch(fetchDeployments(organizationId));
      dispatch(fetchAllBuilds());
      dispatch(fetchAllBehaviorConfigs());
      dispatch(fetchUnreleasedS3Builds());
      dispatch(fetchOrganization(organizationId));
    }
  }, [dispatch, deploymentId, organizationId]);

  useEffect(() => {
    if (currentDeployment) {
      setMinHosts(currentDeployment.min_hosts);
      setMaxHosts(currentDeployment.max_hosts);
      setBehaviorConfigId(currentDeployment.behavior_config_id);
      setMinHostsIsNone(
        currentDeployment.min_hosts === null ||
        currentDeployment.min_hosts === undefined
      );
      setMaxHostsIsNone(
        currentDeployment.max_hosts === null ||
        currentDeployment.max_hosts === undefined
      );
      setDisablePhantomSessions(
        currentDeployment.disable_phantom_sessions || false
      );
      setBuildSelection(
        currentDeployment.build_id
          ? `registry:${currentDeployment.build_id}`
          : ""
      );
    }
  }, [currentDeployment]);

  useEffect(() => {
    // If a toast message was passed via navigation state, show it.
    if (location.state && (location.state as any).toastMessage) {
      toast.success((location.state as any).toastMessage);
      // Clear the state so the toast isn't shown again.
      navigate(location.pathname, { replace: true });
    }
  }, [location, navigate]);

  const validateHosts = useCallback(() => {
    if (
      !minHostsIsNone &&
      !maxHostsIsNone &&
      minHosts !== undefined &&
      maxHosts !== undefined &&
      minHosts > maxHosts
    ) {
      setHostValidationError(
        "Min hosts must be less than or equal to max hosts"
      );
      return false;
    }
    setHostValidationError("");
    return true;
  }, [minHosts, maxHosts, minHostsIsNone, maxHostsIsNone]);

  useEffect(() => {
    if (isEditing) {
      validateHosts();
    }
  }, [isEditing, validateHosts]);

  const handleEditToggle = () => {
    if (!isLatestDeployment) {
      toast.error("Only the latest deployment can be edited");
      return;
    }

    if (isEditing) {
      // If we're canceling edit mode, reset the values
      if (currentDeployment) {
        setMinHosts(currentDeployment.min_hosts);
        setMaxHosts(currentDeployment.max_hosts);
        setBehaviorConfigId(currentDeployment.behavior_config_id);
        setMinHostsIsNone(
          currentDeployment.min_hosts === null ||
          currentDeployment.min_hosts === undefined
        );
        setMaxHostsIsNone(
          currentDeployment.max_hosts === null ||
          currentDeployment.max_hosts === undefined
        );
        setDisablePhantomSessions(
          currentDeployment.disable_phantom_sessions || false
        );
        setHostValidationError("");
        setBuildSelection(
          currentDeployment.build_id
            ? `registry:${currentDeployment.build_id}`
            : ""
        );
      }
    }
    setIsEditing(!isEditing);
  };

  const handleSave = async () => {
    if (!deploymentId || !organizationId) {
      toast.error("Missing deployment ID or organization ID");
      return;
    }

    if (!isLatestDeployment) {
      toast.error("Only the latest deployment can be edited");
      return;
    }

    if (!validateHosts()) {
      toast.error("Please fix validation errors before saving");
      return;
    }

    setIsSaving(true);
    try {
      let nextBuildId: string | undefined = undefined;

      if (buildSelection.startsWith("registry:")) {
        const selectedId = buildSelection.slice("registry:".length);
        nextBuildId =
          selectedId && selectedId !== currentDeployment?.build_id
            ? selectedId
            : undefined;
      } else if (buildSelection.startsWith("uncut:")) {
        const s3 = decodeURIComponent(buildSelection.slice("uncut:".length));
        const uncut = uncutBuilds.find((u) => u.s3_path === s3);
        if (!uncut) {
          toast.error("Selected uncut build not found");
          return;
        }
        const created = await dispatch(
          createHotBuildFromS3({ uncutBuild: uncut })
        ).unwrap();
        nextBuildId = created.id;
        toast.success("Created hot build from S3");
      } else {
        // no change
        nextBuildId = undefined;
      }

      const updateParams = {
        organization_id: organizationId,
        original_deployment_id: deploymentId,
        min_hosts: minHostsIsNone ? null : minHosts,
        max_hosts: maxHostsIsNone ? null : maxHosts,
        build_id: nextBuildId,
        behavior_config_id:
          behaviorConfigId !== currentDeployment?.behavior_config_id
            ? behaviorConfigId
            : undefined,
        disable_phantom_sessions: disablePhantomSessions,
      };

      const updatedDeployment: any = await dispatch(updateDeployment(updateParams))
        .unwrap();

      toast.success(
        "New deployment created and set as latest for this organization"
      );
      setIsEditing(false);

      // First refresh the data to ensure we have the latest deployments
      await dispatch(fetchDeployments(organizationId!));
      // Then navigate to the new deployment with a forced reload
      window.location.href = `/dashboard/deployments/${updatedDeployment.id}?organization_id=${organizationId}`;
    } catch (error: any) {
      toast.error(`Failed to create new deployment: ${error.message || error}`);
    } finally {
      setIsSaving(false);
    }
  };

  const handlePromoteClick = () => {
    if (!isLatestDeployment) {
      toast.error("Only the latest deployment can be promoted");
      return;
    }

    if (!currentDeployment?.is_testbed_deployment) {
      toast.error("This deployment is already a production deployment");
      return;
    }

    setPromoteDialogOpen(true);
  };

  const handlePromoteConfirm = async () => {
    setPromoteDialogOpen(false);

    if (!deploymentId || !organizationId || !currentDeployment) {
      toast.error("Missing deployment ID, organization ID, or deployment data");
      return;
    }

    setIsPromoting(true);
    try {
      const updateParams = {
        organization_id: organizationId,
        original_deployment_id: deploymentId,
        min_hosts: currentDeployment.min_hosts,
        max_hosts: currentDeployment.max_hosts,
        build_id: currentDeployment.build_id,
        behavior_config_id: currentDeployment.behavior_config_id,
        is_testbed_deployment: false, // Promote to production
        disable_phantom_sessions:
          currentDeployment.disable_phantom_sessions || false,
      };

      const updatedDeployment: any = await dispatch(updateDeployment(updateParams))
        .unwrap();

      toast.success("Deployment promoted to production successfully");

      // First refresh the data to ensure we have the latest deployments
      await dispatch(fetchDeployments(organizationId!));
      // Then navigate to the new deployment with a forced reload
      window.location.href = `/dashboard/deployments/${updatedDeployment.id}?organization_id=${organizationId}`;
    } catch (error: any) {
      toast.error(`Failed to promote deployment: ${error.message || error}`);
    } finally {
      setIsPromoting(false);
    }
  };

  const handlePromoteCancel = () => {
    setPromoteDialogOpen(false);
  };

  const handleViewDeployment = (id: string) => {
    navigate(`/dashboard/deployments/${id}?organization_id=${organizationId}`);
  };

  if (loading && !currentDeployment) {
    return <CircularProgress />;
  }

  if (error) {
    return <div>Error: {error}</div>;
  }

  if (!currentDeployment) {
    return <div>Deployment not found</div>;
  }

  return (
    <Box sx={{ p: 4 }}>
      <ToastContainer position="top-center" autoClose={3000} />
      <Typography variant="h5" gutterBottom>
        Deployment Details
        {currentOrganization && (
          <Typography
            component="span"
            variant="h6"
            sx={{ ml: 2, color: "text.secondary" }}
          >
            — {currentOrganization.name}
          </Typography>
        )}
        {!isLatestDeployment && (
          <Chip
            label="Historical (Read-Only)"
            color="warning"
            size="small"
            sx={{ ml: 2 }}
          />
        )}
        {isLatestDeployment && (
          <Chip
            label="Latest Deployment"
            color="success"
            size="small"
            sx={{ ml: 2 }}
          />
        )}
      </Typography>

      {!isLatestDeployment && (
        <Alert severity="info" sx={{ mb: 2 }}>
          This is a historical deployment and cannot be edited. Only the latest
          deployment for an organization can be modified.
        </Alert>
      )}

      {isEditing && isLatestDeployment && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Saving changes will create a new deployment for this organization and
          set it as the latest.
        </Alert>
      )}

      <TextField
        label="Deployment ID"
        value={currentDeployment.id}
        fullWidth
        margin="normal"
        InputProps={{
          readOnly: true,
          sx: {
            backgroundColor: "#f5f5f5",
            opacity: 0.7,
          },
        }}
        InputLabelProps={{ shrink: true }}
      />

      <TextField
        label="Organization"
        value={
          currentOrganization
            ? `${currentOrganization.name} (${currentDeployment.organization_id})`
            : currentDeployment.organization_id
        }
        fullWidth
        margin="normal"
        InputProps={{
          readOnly: true,
          sx: {
            backgroundColor: "#f5f5f5",
            opacity: 0.7,
          },
        }}
        InputLabelProps={{ shrink: true }}
      />

      {isEditing ? (
        <FormControl fullWidth margin="normal">
          <InputLabel id="build-select-label">Build</InputLabel>
          <Select
            labelId="build-select-label"
            value={buildSelection}
            onChange={(e) => setBuildSelection(e.target.value)}
            label="Build"
            required
            MenuProps={{ style: { maxHeight: 520 } }}
          >
            <ListSubheader disableSticky>Uncut S3 builds (will take a long time to sync upon save)</ListSubheader>
            {uncutBuilds && uncutBuilds.length > 0 ? (
              uncutBuilds.map((u) => (
                <MenuItem
                  key={u.s3_path}
                  value={`uncut:${encodeURIComponent(u.s3_path)}`}
                >
                  {u.build_name} — {u.s3_path}
                </MenuItem>
              ))
            ) : (
              <MenuItem disabled>No uncut S3 builds found</MenuItem>
            )}

            <ListSubheader disableSticky>Registry builds (Hot only)</ListSubheader>
            {buildLoading ? (
              <MenuItem disabled>Loading builds...</MenuItem>
            ) : hotRegistryBuilds.length > 0 ? (
              hotRegistryBuilds.map((build) => (
                <MenuItem key={build.id} value={`registry:${build.id}`}>
                  {build.build_name} ({build.id})
                </MenuItem>
              ))
            ) : (
              <MenuItem disabled>No hot registry builds available</MenuItem>
            )}
          </Select>
          <FormHelperText>
            Choose a registry build or an uncut S3 build (we’ll create a hot
            build on save).
          </FormHelperText>
        </FormControl>
      ) : (
        <Grid container spacing={2} alignItems="center">
          <Grid item xs>
            <TextField
              label="Build"
              value={
                currentBuild
                  ? `${currentBuild.build_name} (${currentDeployment.build_id})`
                  : currentDeployment.build_id
              }
              fullWidth
              margin="normal"
              InputProps={{
                readOnly: true,
                sx: {
                  backgroundColor: "#f5f5f5",
                  opacity: 0.7,
                },
              }}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item>
            <Button
              variant="outlined"
              onClick={() =>
                navigate(`/dashboard/builds/${currentDeployment.build_id}`)
              }
            >
              View Build
            </Button>
          </Grid>
        </Grid>
      )}


      {isEditing ? (
        <FormControl fullWidth margin="normal">
          <InputLabel id="behavior-config-select-label">
            Behavior Config
          </InputLabel>
          <Select
            labelId="behavior-config-select-label"
            value={behaviorConfigId}
            onChange={(e) => setBehaviorConfigId(e.target.value)}
            label="Behavior Config"
            required
          >
            {behaviorConfigLoading ? (
              <MenuItem disabled>Loading behavior configs...</MenuItem>
            ) : behaviorConfigs.length > 0 ? (
              behaviorConfigs.map((config) => (
                <MenuItem key={config.id} value={config.id}>
                  {config.behavior_name} ({config.id})
                  {config.demo_mode ? ` - ${config.demo_mode}` : ""}
                </MenuItem>
              ))
            ) : (
              <MenuItem disabled>No behavior configs available</MenuItem>
            )}
          </Select>
          <FormHelperText>Select a behavior configuration</FormHelperText>
        </FormControl>
      ) : (
        <Grid container spacing={2} alignItems="center">
          <Grid item xs>
            <TextField
              label="Behavior Config ID"
              value={currentDeployment.behavior_config_id}
              fullWidth
              margin="normal"
              InputProps={{
                readOnly: true,
                sx: {
                  backgroundColor: "#f5f5f5",
                  opacity: 0.7,
                },
              }}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item>
            <Button
              variant="outlined"
              onClick={() =>
                navigate(
                  `/dashboard/behavior-configs/${currentDeployment.behavior_config_id}`
                )
              }
            >
              View Config
            </Button>
          </Grid>
        </Grid>
      )}

      <Box>
        <TextField
          label="Min Hosts"
          value={
            isEditing
              ? minHostsIsNone
                ? ""
                : minHosts !== undefined
                  ? minHosts.toString()
                  : ""
              : currentDeployment.min_hosts !== undefined &&
                currentDeployment.min_hosts !== null
                ? currentDeployment.min_hosts
                : "None"
          }
          onChange={(e) => {
            const value = e.target.value;
            if (value === "") {
              setMinHosts(undefined);
            } else if (/^\d+$/.test(value)) {
              const numValue = parseInt(value, 10);
              if (numValue >= 0) {
                setMinHosts(numValue);
              }
            }
          }}
          fullWidth
          margin="normal"
          disabled={isEditing && minHostsIsNone}
          error={!!hostValidationError && !minHostsIsNone && !maxHostsIsNone}
          helperText={
            !minHostsIsNone && !maxHostsIsNone && hostValidationError
              ? hostValidationError
              : isEditing && !minHostsIsNone
                ? "Enter a number 0 or greater"
                : ""
          }
          InputProps={{
            readOnly: !isEditing,
            sx: {
              backgroundColor:
                isEditing && !minHostsIsNone ? "white" : "#f5f5f5",
              opacity: isEditing && !minHostsIsNone ? 1 : 0.7,
            },
          }}
          InputLabelProps={{ shrink: true }}
        />
        {isEditing && (
          <FormControlLabel
            control={
              <Checkbox
                checked={minHostsIsNone}
                onChange={(e) => {
                  setMinHostsIsNone(e.target.checked);
                  if (e.target.checked) {
                    setMinHosts(undefined);
                  } else {
                    setMinHosts(1);
                  }
                }}
              />
            }
            label="Set to None"
          />
        )}
      </Box>

      <Box>
        <TextField
          label="Max Hosts"
          value={
            isEditing
              ? maxHostsIsNone
                ? ""
                : maxHosts !== undefined
                  ? maxHosts.toString()
                  : ""
              : currentDeployment.max_hosts !== undefined &&
                currentDeployment.max_hosts !== null
                ? currentDeployment.max_hosts
                : "None"
          }
          onChange={(e) => {
            const value = e.target.value;
            if (value === "") {
              setMaxHosts(undefined);
            } else if (/^\d+$/.test(value)) {
              const numValue = parseInt(value, 10);
              if (numValue >= 0) {
                setMaxHosts(numValue);
              }
            }
          }}
          fullWidth
          margin="normal"
          disabled={isEditing && maxHostsIsNone}
          error={!!hostValidationError && !minHostsIsNone && !maxHostsIsNone}
          helperText={
            !minHostsIsNone && !maxHostsIsNone && hostValidationError
              ? hostValidationError
              : isEditing && !maxHostsIsNone
                ? "Enter a number 0 or greater"
                : ""
          }
          InputProps={{
            readOnly: !isEditing,
            sx: {
              backgroundColor:
                isEditing && !maxHostsIsNone ? "white" : "#f5f5f5",
              opacity: isEditing && !maxHostsIsNone ? 1 : 0.7,
            },
          }}
          InputLabelProps={{ shrink: true }}
        />
        {isEditing && (
          <FormControlLabel
            control={
              <Checkbox
                checked={maxHostsIsNone}
                onChange={(e) => {
                  setMaxHostsIsNone(e.target.checked);
                  if (e.target.checked) {
                    setMaxHosts(undefined);
                  } else {
                    setMaxHosts(1);
                  }
                }}
              />
            }
            label="Set to None"
          />
        )}
      </Box>

      {isEditing ? (
        <FormControlLabel
          sx={{ mt: 2 }}
          control={
            <Checkbox
              checked={disablePhantomSessions}
              onChange={(e) => {
                setDisablePhantomSessions(e.target.checked);
              }}
            />
          }
          label="Disable Phantom Sessions"
        />
      ) : (
        <TextField
          label="Disable Phantom Sessions"
          value={currentDeployment.disable_phantom_sessions ? "Yes" : "No"}
          fullWidth
          margin="normal"
          InputProps={{
            readOnly: true,
            sx: {
              backgroundColor: "#f5f5f5",
              opacity: 0.7,
            },
          }}
          InputLabelProps={{ shrink: true }}
        />
      )}

      <TextField
        label="Created At"
        value={new Date(currentDeployment.created_at).toLocaleString()}
        fullWidth
        margin="normal"
        InputProps={{
          readOnly: true,
          sx: {
            backgroundColor: "#f5f5f5",
            opacity: 0.7,
          },
        }}
        InputLabelProps={{ shrink: true }}
      />

      <TextField
        label="Testbed Deployment"
        value={currentDeployment.is_testbed_deployment ? "Yes" : "No"}
        fullWidth
        margin="normal"
        InputProps={{
          readOnly: true,
          sx: {
            backgroundColor: "#f5f5f5",
            opacity: 0.7,
          },
        }}
        InputLabelProps={{ shrink: true }}
        helperText={
          currentDeployment.is_testbed_deployment
            ? "This is a testbed deployment. Use 'Promote to Production' to convert it."
            : "This is a production deployment"
        }
      />

      {currentDeployment.supplanted_unreal_app_deployment_id && (
        <TextField
          label="Supplanted Deployment ID"
          value={currentDeployment.supplanted_unreal_app_deployment_id}
          fullWidth
          margin="normal"
          InputProps={{
            readOnly: true,
            sx: {
              backgroundColor: "#f5f5f5",
              opacity: 0.7,
            },
          }}
          InputLabelProps={{ shrink: true }}
        />
      )}

      {currentDeployment.created_by_user_id && (
        <TextField
          label="Created By User ID"
          value={currentDeployment.created_by_user_id}
          fullWidth
          margin="normal"
          InputProps={{
            readOnly: true,
            sx: {
              backgroundColor: "#f5f5f5",
              opacity: 0.7,
            },
          }}
          InputLabelProps={{ shrink: true }}
        />
      )}

      <Box display="flex" alignItems="center" marginTop={"16px"}>
        {isEditing ? (
          <>
            <Button
              disabled={!!hostValidationError || isSaving}
              variant="contained"
              color="primary"
              onClick={handleSave}
              startIcon={isSaving ? <CircularProgress size={18} /> : undefined}
            >
              {isSaving ? "Saving…" : "Save as New Deployment"}
            </Button>
            <Button
              variant="contained"
              color="warning"
              onClick={handleEditToggle}
              style={{ marginLeft: "8px" }}
              disabled={isSaving}
            >
              Cancel
            </Button>
          </>
        ) : (
          <>
            {isLatestDeployment && (
              <Button
                variant="contained"
                color="primary"
                onClick={handleEditToggle}
                disabled={isSaving || isPromoting}
              >
                Edit
              </Button>
            )}
            {isLatestDeployment && currentDeployment.is_testbed_deployment && (
              <Button
                variant="contained"
                color="success"
                onClick={handlePromoteClick}
                style={{ marginLeft: "8px" }}
                disabled={isSaving || isPromoting}
                startIcon={isPromoting ? <CircularProgress size={18} /> : undefined}
              >
                {isPromoting ? "Promoting…" : "Promote to Production"}
              </Button>
            )}
            <Button
              variant="contained"
              color="warning"
              onClick={() =>
                navigate(`/dashboard/organizations/${organizationId}`)
              }
              style={{ marginLeft: "8px" }}
              disabled={isSaving || isPromoting}
            >
              Back to Organization
            </Button>
          </>
        )}
      </Box>

      <Divider sx={{ my: 4 }} />

      {/* Deployment Chain */}
      <Typography variant="h5" gutterBottom>
        Deployment History
      </Typography>
      <Paper elevation={3} sx={{ p: 2, mb: 4 }}>
        <List>
          {deploymentChain.map((deployment) => (
            <ListItem
              key={deployment.id}
              sx={{
                mb: 1,
                backgroundColor:
                  deployment.id === currentDeployment.id
                    ? "#f0f7ff"
                    : "inherit",
                borderRadius: "4px",
              }}
              secondaryAction={
                <Box>
                  {deployment.id !== currentDeployment.id && (
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => handleViewDeployment(deployment.id)}
                      sx={{ mr: 1 }}
                    >
                      View
                    </Button>
                  )}
                  <Button
                    variant="outlined"
                    size="small"
                    color="secondary"
                    onClick={() =>
                      navigate(`/dashboard/builds/${deployment.build_id}`)
                    }
                  >
                    View Build
                  </Button>
                </Box>
              }
            >
              <ListItemText
                primary={
                  <Box display="flex" alignItems="center">
                    <Typography variant="body1" component="span">
                      {deployment.id}
                    </Typography>
                    {deployment.id === currentDeployment.id && (
                      <Chip
                        label="Current"
                        color="primary"
                        size="small"
                        sx={{ ml: 2 }}
                      />
                    )}
                    {deployment.id === deploymentChain[0]?.id && (
                      <Chip
                        label="Latest"
                        color="success"
                        size="small"
                        sx={{ ml: 2 }}
                      />
                    )}
                  </Box>
                }
                secondary={
                  <>
                    <Typography variant="body2" component="span">
                      Created:{" "}
                      {new Date(deployment.created_at).toLocaleString()}
                    </Typography>
                    <br />
                    <Typography variant="body2" component="span">
                      Build:{" "}
                      {builds?.find((b) => b.id === deployment.build_id)
                        ?.build_name || deployment.build_id}
                    </Typography>
                    <br />
                    <Typography variant="body2" component="span">
                      Behavior Config: {deployment.behavior_config_id}
                    </Typography>
                    <br />
                    <Typography variant="body2" component="span">
                      Testbed: {deployment.is_testbed_deployment ? "Yes" : "No"}
                    </Typography>
                    <br />
                    <Typography variant="body2" component="span">
                      Disable Phantom Sessions:{" "}
                      {deployment.disable_phantom_sessions ? "Yes" : "No"}
                    </Typography>
                  </>
                }
              />
            </ListItem>
          ))}
        </List>
      </Paper>

      {/* Confirmation Dialog */}
      <Dialog
        open={promoteDialogOpen}
        onClose={handlePromoteCancel}
        aria-labelledby="promote-dialog-title"
        aria-describedby="promote-dialog-description"
      >
        <DialogTitle id="promote-dialog-title">
          Confirm Promotion to Production
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="promote-dialog-description">
            Are you sure you want to promote this testbed deployment to
            production? This action will create a new deployment with production
            status. This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handlePromoteCancel} color="primary">
            Cancel
          </Button>
          <Button
            onClick={handlePromoteConfirm}
            color="primary"
            variant="contained"
            autoFocus
            disabled={isPromoting}
            startIcon={isPromoting ? <CircularProgress size={18} /> : undefined}
          >
            {isPromoting ? "Promoting…" : "Promote to Production"}
          </Button>
        </DialogActions>
      </Dialog>

      <Backdrop
        open={isSaving || isPromoting}
        sx={{ zIndex: (theme) => theme.zIndex.drawer + 1, color: "#fff" }}
        aria-live="polite"
        aria-busy={isSaving || isPromoting}
      >
        <Box display="flex" flexDirection="column" alignItems="center" gap={2}>
          <CircularProgress />
          <Typography variant="body2">
            {isSaving
              ? "Saving new deployment…"
              : isPromoting
                ? "Promoting deployment…"
                : ""}
          </Typography>
        </Box>
      </Backdrop>
    </Box>
  );
};

export default DeploymentView;
